import type { ComponentType } from 'react';
import get from 'lodash/get';
import { connect } from 'react-redux';

/**
 * Replacement for `formValues()` from bnc-react-forms that reads form values
 * directly from Redux state via `connect()`.
 *
 * After the React 18 upgrade, `formValues()` (redux-form 8.1.0) throws
 * "formValues() must be used inside a React tree decorated with reduxForm()"
 * because the legacy ReduxFormContext does not propagate reliably in React 18.
 *
 * The previous implementation used an error boundary to catch-and-fallback,
 * but React 18 always logs caught errors to the console. This version avoids
 * the throw entirely by reading the same values straight from the Redux store.
 */
const buildMapStateToProps = (fields: string[]) => (state) =>
  fields.reduce<Record<string, unknown>>((acc, field) => {
    acc[field] = get(state, `reduxFormReducer.baoForms.values.${field}`);
    return acc;
  }, {});

export const formValues = (...fields: string[]) => {
  return (WrappedComponent: ComponentType<any>) => {
    const ConnectedComponent = connect(buildMapStateToProps(fields))(WrappedComponent);
    const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    ConnectedComponent.displayName = `FormValues(${wrappedComponentName})`;
    return ConnectedComponent;
  };
};
